import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { groupBy } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import type { SourceId } from '../../constants';
import { getSourceMetaValue } from '../../constants';
import type { DefaultComponentProps } from '../types';
import { useSocialFeedSettings } from '../utils';

const ALL_TAB = {
  type: 'all',
  id: 'all',
  name: 'All'
} as const;

type AllTabType = typeof ALL_TAB;

type SourceTabType = {
  type: SourceId;
  id: string;
  name: string;
};

type TabType = AllTabType | SourceTabType;

type TabsContextType = {
  currentTab: TabType;
  tabList: TabType[];
  setCurrentTab: (tabId: TabType['id']) => void;
};

const TabsContext = createContext<TabsContextType>({
  currentTab: ALL_TAB,
  tabList: [ALL_TAB],
  setCurrentTab: () => {}
});

export function TabsProvider({ children }: DefaultComponentProps) {
  const { t } = useTranslation();
  const translatedAllTab = useMemo(
    () => ({ ...ALL_TAB, name: t('All') }) as AllTabType,
    [t]
  );
  const { sources, tabsGrouping } = useSocialFeedSettings();

  const tabList = useMemo(() => {
    const sourcesTabs = (() => {
      if (tabsGrouping) {
        const groups = groupBy(sources, (item) => item.platform);
        const platforms = Object.keys(groups) as SourceId[];

        return platforms.map((platform) => {
          const { title } = getSourceMetaValue(platform);

          return {
            type: platform,
            id: platform,
            name: title!
          };
        });
      }

      return sources.map(({ platform, id, name }) => ({
        type: platform,
        id: `${platform}-${id}`,
        name
      }));
    })();

    if (sourcesTabs.length === 1) {
      return sourcesTabs;
    }

    return [translatedAllTab, ...sourcesTabs];
  }, [translatedAllTab, sources, tabsGrouping]);

  const [currentTab, setCurrentTab] = useState<TabType>(tabList[0]);

  const setCurrentTabId = useCallback(
    (tabId: TabType['id']) => {
      const tab = tabList.find(({ id }) => id === tabId) || translatedAllTab;

      setCurrentTab(tab);
    },
    [translatedAllTab, tabList]
  );

  useEffect(() => {
    setCurrentTab(tabList[0]);
  }, [tabsGrouping, tabList.length]);

  const value = useMemo(
    () => ({
      tabList,
      currentTab,
      setCurrentTab: setCurrentTabId
    }),
    [currentTab, tabList, setCurrentTabId]
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

export function useTabs() {
  return useContext(TabsContext);
}
